const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/sro/eCheckin/sections/KontaktPerson.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import textMap from '../translations';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import textMapCommon from '../../../common/translations';
import {
    AddressBlock,
    AddressFieldsConfig,
    defaultAddressFieldsConfig,
} from 'App/components/eCheckin/components/fields/AddressBlock';
import { ECheckinSectionProps, FieldPathModifier } from 'App/components/eCheckin/types';
import { CIS4mPhoneNumberField } from '@cistec/common_web/modules/CIS4m/components/CIS4mPhoneNumberField';
import { validatePhoneNumber } from '@cistec/common_web/components/Forms/utils/validation';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import CIS4mSingleCheckbox from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';
import _ from 'lodash';
import { BeziehungField } from 'App/components/eCheckin/components/fields/metakat/BeziehungField';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { useIntl } from 'react-intl';
import { createFieldPath } from 'App/components/eCheckin/utils';
import { valueToBoolean } from '@cistec/common_web/modules/CIS4m/utils/data';

const firstContactPathModifier: FieldPathModifier = (fieldPath) => `angehoerige1.${fieldPath}`;
const secondContactPathModifier: FieldPathModifier = (fieldPath) => `angehoerige2.${fieldPath}`;

type KontaktPersonBlockProps = {
    basePath: string;
    fieldPathModifier?: FieldPathModifier;
    hidden?: boolean;
    required?: boolean;
};

const KontaktPersonBlock = forwardRef((props: KontaktPersonBlockProps, ref: any) => {
    const { basePath, fieldPathModifier, required = false } = props;

    const hidden = props.hidden ?? false;
    const kontaktPersonBlockRef = useCIS4m(ref);

    const PATH_RELATION = createFieldPath(basePath, 'relation', fieldPathModifier);
    const PATH_RELATION_OTHER = createFieldPath(basePath, 'relationOther', fieldPathModifier);
    const PATH_FIRST_NAME = createFieldPath(basePath, 'firstname', fieldPathModifier);
    const PATH_LAST_NAME = createFieldPath(basePath, 'lastname', fieldPathModifier);
    const PATH_TEL = createFieldPath(basePath, 'tel', fieldPathModifier);

    const addressFieldsConfig: AddressFieldsConfig = {
        streetAndNumber: {
            path: fieldPathModifier ? fieldPathModifier(defaultAddressFieldsConfig.streetAndNumber.path) : undefined,
        },
        additionalInfo: {
            included: false,
        },
        postalCode: {
            path: fieldPathModifier ? fieldPathModifier(defaultAddressFieldsConfig.postalCode.path) : undefined,
        },
        location: {
            path: fieldPathModifier ? fieldPathModifier(defaultAddressFieldsConfig.location.path) : undefined,
        },
        country: {
            included: false,
        },
    };

    const formData = useCIS4mData();
    const showRelationOther = _.get(formData, PATH_RELATION) === 'andere';

    return (
        <CIS4mBlock ref={kontaktPersonBlockRef} hidden={hidden}>
            <BeziehungField label={textMap.relation} path={PATH_RELATION} required={required} />
            <CIS4mTextField
                hidden={!showRelationOther}
                label={textMap.relationOther}
                path={PATH_RELATION_OTHER}
                required={showRelationOther}
            />
            <CIS4mTextField label={textMapCommon.lastname} path={PATH_LAST_NAME} required={required} />
            <CIS4mTextField label={textMapCommon.firstname} path={PATH_FIRST_NAME} required={required} />
            <AddressBlock basePath={basePath} required={required} fieldsConfig={addressFieldsConfig} />
            <CIS4mPhoneNumberField
                label={textMapCommon.tel}
                path={PATH_TEL}
                required={required}
                checkValidity={validatePhoneNumber}
            />
        </CIS4mBlock>
    );
});

KontaktPersonBlock.displayName = 'KontaktPersonBlock';

export const KontaktPerson = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);
    const { formatMessage } = useIntl();

    const PATH_REL1_NO_SPECIFIED = createFieldPath(path, 'noSpecified', firstContactPathModifier);
    const PATH_REL2_NO_SPECIFIED = createFieldPath(path, 'noSpecified', secondContactPathModifier);

    const formData = useCIS4mData();
    const noSpecified = _.get(formData, PATH_REL1_NO_SPECIFIED) === '1';
    const addRel2 = _.get(formData, PATH_REL2_NO_SPECIFIED) === '1';

    const eCheckinSectionRef = useCIS4m(ref);

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={props.path} title={title}>
            <h1>{title}</h1>

            <CIS4mSingleCheckbox
                label={textMap.keineAngaben}
                path={PATH_REL1_NO_SPECIFIED}
                parseValue={valueToBoolean}
            />

            <CIS4mBlock hidden={noSpecified}>
                <h4>{formatMessage(textMap.relative1Title)}</h4>
                <KontaktPersonBlock
                    basePath={path}
                    fieldPathModifier={firstContactPathModifier}
                    required={!noSpecified}
                />
            </CIS4mBlock>

            <CIS4mBlock hidden={noSpecified}>
                <CIS4mSingleCheckbox label={textMap.relative2Title} path={PATH_REL2_NO_SPECIFIED} />
                <CIS4mBlock hidden={!addRel2}>
                    <KontaktPersonBlock basePath={path} fieldPathModifier={secondContactPathModifier} required={!addRel2} />
                </CIS4mBlock>
            </CIS4mBlock>
        </ECheckinSection>
    );
});

KontaktPerson.displayName = 'KontaktPerson';

export default KontaktPerson;
